import { createTheme } from '@mui/material/styles';
import type { AppDir } from '../context/DirectionContext';
import { T } from './tokens';

export function createAppTheme(direction: AppDir = 'ltr') { return createTheme({ direction,
  palette: {
    primary:    { main: T.ink,    contrastText: T.bg },
    secondary:  { main: T.tierTop, contrastText: T.bg },
    error:      { main: T.danger, contrastText: T.bg },
    warning:    { main: T.tierMid, contrastText: T.ink },
    info:       { main: T.textMuted, contrastText: T.bg },
    success:    { main: T.tierTop, contrastText: T.bg },
    background: { default: T.bg, paper: T.surface },
    text:       { primary: T.text, secondary: T.textMuted },
    divider:    T.border,
  },

  typography: {
    fontFamily: `'Geist', ui-sans-serif, system-ui, -apple-system, sans-serif`,
    h1: { fontSize: 26, fontWeight: 500, letterSpacing: '-0.02em', lineHeight: 1.15 },
    h2: { fontSize: 20, fontWeight: 500, letterSpacing: '-0.015em' },
    h3: { fontSize: 18, fontWeight: 500, letterSpacing: '-0.01em' },
    h4: { fontSize: 16, fontWeight: 500 },
    h5: { fontSize: 14, fontWeight: 500 },
    h6: { fontSize: 13, fontWeight: 500 },
    body1: { fontSize: 14, lineHeight: 1.5 },
    body2: { fontSize: 13, color: T.textMuted },
    caption: { fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.textSubtle },
    button: { fontSize: 12, fontWeight: 500, letterSpacing: '0.02em', textTransform: 'none' },
  },

  shape: { borderRadius: 0 },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          fontFeatureSettings: `'ss01', 'cv11'`,
          backgroundColor: T.bg,
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          letterSpacing: '0.02em',
          borderRadius: 2,
          boxShadow: 'none',
          height: 32,
          fontSize: 12,
          padding: '0 14px',
          '&:hover': { boxShadow: 'none' },
        },
        // MUI's icon spacing is physical (left/right) and the app has no RTL style
        // plugin, so mirror it in Hebrew to keep icons off the label.
        ...(direction === 'rtl' && {
          startIcon: { marginLeft: 8, marginRight: -4 },
          endIcon: { marginRight: 8, marginLeft: -4 },
        }),
        containedPrimary: {
          backgroundColor: T.ink,
          border: `1px solid ${T.ink}`,
          color: T.bg,
          '&:hover': { backgroundColor: T.text },
        },
        outlinedPrimary: {
          borderColor: T.borderInput,
          color: T.text,
          '&:hover': { backgroundColor: T.surfaceAlt, borderColor: T.borderInput },
        },
      },
      defaultProps: { disableElevation: true },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: 'none',
          border: `1px solid ${T.border}`,
        },
        elevation0: { boxShadow: 'none' },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: 'none',
          border: `1px solid ${T.border}`,
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: 11,
          borderRadius: 2,
          height: 22,
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: T.borderInput },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.textSubtle },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.ink, borderWidth: 1 },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: 12,
          color: T.textSubtle,
          '&.Mui-focused': { color: T.ink },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: { fontSize: 13, paddingTop: 6, paddingBottom: 6 },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 13,
          '&.Mui-selected': { backgroundColor: T.surfaceSel },
          '&.Mui-selected:hover': { backgroundColor: T.surfaceSel },
          '&:hover': { backgroundColor: T.surfaceAlt },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 0, boxShadow: 'none', border: `1px solid ${T.border}` },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: { fontWeight: 500, fontSize: 16, paddingBottom: 8 },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 0 },
        // Same physical-margin issue as MuiButton icons: mirror in Hebrew.
        ...(direction === 'rtl' && { icon: { marginRight: 0, marginLeft: 12 } }),
      },
    },

    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, fontSize: 13, minHeight: 40 },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: { height: 1, backgroundColor: T.ink },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: { borderColor: T.border },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: { boxShadow: 'none' },
      },
    },

    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          '&:before': { display: 'none' },
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: { borderRadius: 0 },
      },
    },

    MuiSnackbar: {
      defaultProps: { anchorOrigin: { vertical: 'bottom', horizontal: 'center' } },
    },
  },
}); }

/** Convenience default export kept for any non-direction-aware import sites */
export const theme = createAppTheme('rtl');
